import { type FC, useCallback } from 'react';

import type { Editor } from '@tiptap/react';

import type { CommunityPostComposerProps, ComposerGif } from '../../CommunityPostComposer.type';
import { DEFAULT_GIF_SEARCH_DEBOUNCE_MS, DEFAULT_GIF_SEARCH_LIMIT, tr } from '../constants';
import { GifPicker } from '../GifPicker';
import { landOnTextblock } from '../landOnTextblock';

interface MediaGifTabProps {
  editor: Editor;
  translations?: Record<string, string>;
  onSearchGifs: NonNullable<CommunityPostComposerProps['onSearchGifs']>;
  onInserted: () => void;
  gifSearchLimit?: number;
  gifSearchDebounceMs?: number;
}

export const MediaGifTab: FC<MediaGifTabProps> = ({
  editor,
  translations,
  onSearchGifs,
  onInserted,
  gifSearchLimit = DEFAULT_GIF_SEARCH_LIMIT,
  gifSearchDebounceMs = DEFAULT_GIF_SEARCH_DEBOUNCE_MS,
}) => {
  const handleSelect = useCallback(
    (gif: ComposerGif) => {
      const pos = editor.state.selection.to;
      editor
        .chain()
        .focus()
        .insertContentAt(pos, { type: 'image', attrs: { src: gif.url, alt: gif.title ?? '' } })
        .command(landOnTextblock)
        .run();
      onInserted();
    },
    [editor, onInserted],
  );

  return (
    <GifPicker
      onSearchGifs={onSearchGifs}
      onSelect={handleSelect}
      gifSearchLimit={gifSearchLimit}
      gifSearchDebounceMs={gifSearchDebounceMs}
      labels={{
        searchPlaceholder: tr(translations, 'community.composer.gif_search_placeholder'),
        loading: tr(translations, 'community.composer.gif_loading'),
        error: tr(translations, 'community.composer.gif_error'),
        noResults: tr(translations, 'community.composer.gif_no_results'),
        poweredBy: tr(translations, 'community.composer.gif_powered_by'),
      }}
    />
  );
};
